<script setup lang="ts">
import SearchableSelectList, { type ISearchableSelectItem } from '@/components/createdUi/SearchableSelectList.vue'
import type { IImportColumnSelection } from '../types/IImportColumnSelection'
import type { IImportUploadResponse } from '../types/IImportUploadResponse'
import { columnLetter } from '../utils/columnLetter'
import ImportFileChip from './ImportFileChip.vue'

const props = defineProps<{
  fileName: string
  fileSize: number
  upload: IImportUploadResponse
  selections: IImportColumnSelection[]
  errors: Record<number, string>
}>()

const emit = defineEmits<{
  'update:selections': [value: IImportColumnSelection[]]
  remove: []
  back: []
  next: []
}>()

const optionNames = computed(() => props.upload.required_columns.map(column => column.name))

const selectItems = computed<ISearchableSelectItem[]>(() =>
  optionNames.value.map(name => ({ id: name, label: name })),
)

const openIndex = ref<number | null>(null)

function setMenu(index: number, open: boolean) {
  openIndex.value = open ? index : (openIndex.value === index ? null : openIndex.value)
}

function isAuto(item: IImportColumnSelection) {
  if (!item.canonical)
    return false
  if (!props.upload.recognized_columns.includes(item.canonical))
    return false
  return props.upload.mapping[item.canonical] === item.header
}

function onPick(index: number, value: string | null) {
  const next = props.selections.map(item => ({ ...item }))
  if (value) {
    next.forEach((item, itemIndex) => {
      if (itemIndex !== index && item.canonical === value)
        item.canonical = null
    })
  }
  const row = next[index]
  if (row)
    row.canonical = value
  emit('update:selections', next)
}

function columnSample(index: number) {
  const column = props.upload.file_columns[index]
  if (!column)
    return ''
  const fromColumn = column.sample || column.example
  if (fromColumn)
    return String(fromColumn)
  return props.upload.first_row?.[column.name] ?? ''
}

function onSelectItem(index: number, item: ISearchableSelectItem) {
  onPick(index, String(item.id))
  openIndex.value = null
}
</script>

<template>
  <div class="import-step-map d-flex flex-column ga-6">
    <ImportFileChip
      :name="fileName"
      :size="fileSize"
      @remove="emit('remove')"
    />

    <div class="d-flex flex-column ga-4 import-map-block">
      <VCard class="import-map-card pa-4 d-flex flex-column">
        <div class="import-map-scroll">
          <div class="import-map-head d-flex flex-column ga-4">
            <div class="d-flex flex-column ga-1">
              <span class="text-h5 import-text">Сопоставление столбцов</span>
              <span class="body-1 import-text">Выберите значения для полей, которые не определились автоматически.</span>
            </div>
            <div class="import-map-labels">
              <span class="import-col-label">Поля из вашего файла</span>
              <span />
              <span class="import-col-label">Поля в системе DAO-MED.ru</span>
            </div>
          </div>
          <div class="import-map">
            <template
              v-for="(item, index) in selections"
              :key="`${item.header}-${index}`"
            >
          <div class="import-excel-card border rounded overflow-hidden">
            <div class="import-excel-sheet d-flex align-center ga-2 px-3 py-2 body-1-500 text-on-primary">
              <VIcon
                icon="ri-file-excel-2-line"
                size="18"
              />
              <span>Лист 1 {{ columnLetter(index) }}1:{{ columnLetter(index) }}2</span>
            </div>
            <div class="import-excel-corner" />
            <div class="import-excel-letter text-center py-1">
              {{ columnLetter(index) }}
            </div>
            <div class="import-excel-num">
              1
            </div>
            <div class="import-excel-header pa-2 body-1-500">
              {{ item.header }}
            </div>
            <div class="import-excel-num">
              2
            </div>
            <div class="import-excel-sample pa-2 body-1-500 text-medium-emphasis">
              {{ columnSample(index) }}
            </div>
          </div>
          <VIcon
            icon="ri-arrow-right-line"
            class="import-map-arrow"
            color="secondary"
          />
          <div class="import-map-select">
            <div class="import-map-select-box">
              <VMenu
                :model-value="openIndex === index"
                :close-on-content-click="false"
                :max-height="350"
                location="bottom"
                content-class="entity-form-menu"
                @update:model-value="setMenu(index, $event)"
              >
                <template #activator="{ props: menuProps }">
                  <VTextField
                    :model-value="item.canonical"
                    class="invisible-readonly cursor-pointer"
                    :append-inner-icon="openIndex === index ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"
                    variant="outlined"
                    density="comfortable"
                    placeholder="Выберите поле"
                    readonly
                    clearable
                    hide-details
                    :error="Boolean(errors[index])"
                    v-bind="menuProps"
                    @click:append-inner.stop="setMenu(index, openIndex !== index)"
                    @click:clear="onPick(index, null)"
                  />
                </template>
                <SearchableSelectList
                  :items="selectItems"
                  placeholder="Поиск..."
                  @select="onSelectItem(index, $event)"
                />
              </VMenu>
              <div
                v-if="isAuto(item)"
                class="import-map-status d-flex align-center ga-1 text-success"
              >
                <VIcon icon="ri-checkbox-circle-line" size="20" color="success" />
                <span class="body-1">Определено автоматически</span>
              </div>
              <div
                v-else-if="errors[index]"
                class="import-map-status d-flex align-center ga-1 text-error"
              >
                <VIcon icon="ri-alert-line" size="20" color="error" />
                <span class="body-1">Выберите поле из списка</span>
              </div>
            </div>
          </div>
            </template>
          </div>
        </div>
      </VCard>

      <div class="d-flex align-center justify-space-between">
        <VBtn
          class="import-step-btn"
          variant="outlined"
          color="primary"
          prepend-icon="ri-arrow-left-line"
          @click="emit('back')"
        >
          Назад
        </VBtn>
        <VBtn
          class="import-step-btn"
          variant="outlined"
          color="primary"
          append-icon="ri-arrow-right-line"
          @click="emit('next')"
        >
          Продолжить
        </VBtn>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.import-step-map {
  max-width: 900px;
  /* Шапка 64px и степпер остаются снаружи, скролл только у карточки */
  max-height: calc(100dvh - 64px - 6.5rem);
  min-height: 0;
}

.import-map-block {
  flex: 1 1 auto;
  min-height: 0;
}

.import-map-card {
  flex: 1 1 auto;
  min-block-size: 0;
  overflow: hidden;
}

.import-map-scroll {
  flex: 1 1 auto;
  min-block-size: 0;
  overflow-y: auto;
}

.import-map-head {
  position: sticky;
  inset-block-start: 0;
  z-index: 1;
  background: rgb(var(--v-theme-surface));
  padding-bottom: 1.5rem;
}

.import-map-labels,
.import-map {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 1.5rem minmax(0, 1fr);
  column-gap: 1.5rem;
}

.import-text {
  color: rgba(var(--v-theme-text-primary), var(--v-high-emphasis-opacity));
}

.import-col-label {
  color: rgba(var(--v-theme-text-primary), var(--v-high-emphasis-opacity));
  font-family: 'Inter', sans-serif;
  font-size: 0.8125rem;
  font-weight: 500;
  letter-spacing: 0.2px;
  line-height: 24px;
  text-transform: uppercase;
}

.import-map {
  align-items: stretch;
  row-gap: 1.5rem;
  /* Подпись под селектом абсолютная и не входит в высоту строки */
  padding-bottom: calc(8px + 1.25rem);
}

.import-excel-card {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  min-inline-size: 0;
}

.import-map-arrow {
  align-self: center;
  justify-self: center;
}

.import-map-select {
  display: flex;
  align-items: center;
}

.import-map-select-box {
  position: relative;
  inline-size: 100%;
}

.import-map-status {
  position: absolute;
  inset-block-start: calc(100% + 8px);
  inset-inline-start: 0;
}

.import-excel-sheet {
  grid-column: 1 / -1;
  background: rgb(var(--v-theme-excel-header));
}

.import-excel-corner,
.import-excel-letter,
.import-excel-num {
  background: rgb(var(--v-theme-excel-axis));
}

.import-excel-letter,
.import-excel-num {
  color: rgba(var(--v-theme-excel-axis-text), var(--v-high-emphasis-opacity));
  font-family: 'Inter', sans-serif;
  font-size: 0.8125rem;
  font-weight: 500;
  letter-spacing: 0.2px;
  line-height: 24px;
  text-align: center;
  text-transform: uppercase;
}

.import-excel-corner,
.import-excel-num {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0;
  min-inline-size: 2rem;
  border-right: 1px solid rgba(var(--v-theme-excel-axis-text), var(--v-border-opacity));
}

.import-excel-letter,
.import-excel-header,
.import-excel-sample,
.import-excel-num {
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.import-excel-header,
.import-excel-sample {
  overflow-wrap: anywhere;
  white-space: normal;
}

.import-excel-header {
  color: rgba(var(--v-theme-text-primary), var(--v-high-emphasis-opacity));
}

.import-excel-sample {
  min-block-size: 2.5rem;
}

.import-step-btn {
  inline-size: 12rem;
  min-inline-size: 12rem;
}
</style>
